'use client'

import { Box, Flex, Group, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { colorMap } from '@/app/[locale]/price/stocks/[symbol]/_components/AnalystRatings'
import {
  ColorChangeCircle,
  SuggestPercentageBar,
  SuggestType,
} from '@/app/[locale]/price/stocks/[symbol]/_components/AnalystRatings/component'
import { AnalystRatingsFallback } from '@/app/[locale]/price/stocks/[symbol]/_components/AnalystRatings/fallback'
import IconTrendingDown from '@/assets/icons/chart-trending-down.svg'
import IconTrendingUp from '@/assets/icons/chart-trending-up.svg'
import IconMinus from '@/assets/icons/icon-minus.svg'
import IconTarget from '@/assets/icons/target.svg'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useStockBySymbol } from '@/services/stock/meta'
import { useStockRatingsBySymbol } from '@/services/stock/ratings'
import { formatPercentageColor, formatPercentageWithPrefixLimit } from '@/utils/number/format'

import AccessInApp from './AccessInApp'

function AnalystRatingsContent() {
  const t = useTranslations('analystRating')
  const { coin } = useCoinContext()
  const { data: ratings } = useStockRatingsBySymbol(coin.symbol)
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY

  const { data: asset } = useStockBySymbol(coin.symbol)

  if (!ratings) return null
  const { buy, hold, sell, target_price, est_return, rating } = ratings

  // Only display if all 3 rating buckets have data
  if (!(buy > 0 && hold > 0 && sell > 0)) return null

  // Only display if Market Cap < USD 300 million
  const marketCap = parseFloat(asset?.market_cap || '0')
  if (marketCap > 0 && marketCap >= 300_000_000) return null

  // // Only display if Target Price < 200% of Yesterday Close
  const yesterdayClose = parseFloat(asset?.previous_closing_price || '0')
  if (yesterdayClose > 0 && target_price >= 2 * yesterdayClose) return null

  const stockSuggest = rating.toLowerCase() as SuggestType

  const returnSuggest = est_return > 0 ? 'buy' : est_return < 0 ? 'sell' : 'hold'
  const estReturn = formatPercentageWithPrefixLimit(est_return, true)
  const estReturnChangeColor = formatPercentageColor(Number(estReturn.replace('%', '')))

  return (
    <Box
      mt={{ base: 40, medium: 80 }}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Flex justify="space-between" align="center" mb={16}>
        <Text component="h2" variant="heading2">
          {t('title')}
        </Text>
        <Box visibleFrom="lg">
          <AccessInApp />
        </Box>
      </Flex>
      <Flex
        direction={{ base: 'column', medium: 'row', large: 'column', contentLimit: 'row' }}
        gap={{ base: 0, medium: 40 }}
        align={{
          base: 'flex-start',
          medium: 'center',
          large: 'flex-start',
          contentLimit: 'flex-start',
        }}
      >
        <Flex gap={{ base: 12, medium: 16 }} py={16} flex={1}>
          <ColorChangeCircle suggest={stockSuggest} w={72} h={72}>
            <Text c={colorMap[stockSuggest].child} variant="heading1">
              {t(stockSuggest)}
            </Text>
          </ColorChangeCircle>
          <Stack gap={12}>
            {[
              { value: buy, suggest: 'buy' },
              { value: hold, suggest: 'hold' },
              { value: sell, suggest: 'sell' },
            ].map(item => (
              <SuggestPercentageBar
                key={item.suggest}
                value={item.value}
                suggest={item.suggest as SuggestType}
              />
            ))}
          </Stack>
        </Flex>
        <Flex py={12} gap={16}>
          <Group gap={8} wrap="nowrap">
            <Flex
              justify="center"
              align="center"
              w={36}
              h={36}
              style={{
                borderRadius: 40,
                background: 'var(--bg-button-tertiary-default)',
              }}
            >
              <IconTarget width={36} height={36} />
            </Flex>
            <Stack w="max-content" gap={0}>
              <Text c="var(--content-secondary)" variant="body">
                {t('target_price')}
              </Text>
              <Text c="var(--content-primary)" variant="heading3">
                {`${currentCurrency.symbol}${(target_price || 0).toFixed(2)}`}
              </Text>
            </Stack>
          </Group>
          <Group gap={8} wrap="nowrap">
            <ColorChangeCircle w={36} h={36} suggest={returnSuggest}>
              {returnSuggest === 'buy' ? (
                <IconTrendingUp width={36} height={36} />
              ) : returnSuggest === 'sell' ? (
                <IconTrendingDown width={36} height={36} />
              ) : (
                <IconMinus width={36} height={36} />
              )}
            </ColorChangeCircle>
            <Stack w="max-content" gap={0}>
              <Text c="var(--content-secondary)" variant="body">
                {t('est_return')}
              </Text>
              <Text c={estReturnChangeColor} variant="heading3">
                {estReturn}
              </Text>
            </Stack>
          </Group>
        </Flex>
      </Flex>
    </Box>
  )
}

function AnalystRatings() {
  return (
    <Suspense fallback={<AnalystRatingsFallback />}>
      <AnalystRatingsContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(AnalystRatings)
